import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Coins, Shield, Package, Compass, Sparkles, Flame, Ticket,
  Heart, Gift, Loader2, Send, Award, Trophy, Hammer, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ICONS = {
  shield: Shield, package: Package, compass: Compass, sparkles: Sparkles,
  flame: Flame, ticket: Ticket,
};

/**
 * The Loot Bazaar — single page that lets hunters spend Loot Coins. Tabs:
 *
 *   power-ups   → cosmetic + gameplay items from the shop
 *   tank        → donate coins to the Community LootFund (1:1, 100c=$1)
 *   flip        → Double or Dust mini-game
 *   gift        → send coins to another hunter
 *   coupons     → my $1-off sponsor coupons (with QR codes for redemption)
 *
 * The HUD up top stays sticky-ish so the balance is always visible.
 */
export default function BazaarPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("power-ups");
  const [shop, setShop] = useState([]);
  const [inv, setInv] = useState(null);
  const [coupons, setCoupons] = useState([]);

  const reload = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/coins/shop"),
        api.get("/coins/inventory"),
        api.get("/coins/coupons/me").catch(() => ({ data: { items: [] } })),
      ]);
      setShop(a.data?.items || []);
      setInv(b.data || null);
      setCoupons(c.data?.items || []);
    } catch {
      setInv({ balance: 0 });
    }
  };
  useEffect(() => { reload(); }, []);

  const balance = inv?.balance ?? 0;
  const streak = inv?.stash_streak ?? 0;

  return (
    <div className="py-10 space-y-6" data-testid="bazaar-page">
      <header className="max-w-3xl mx-auto text-center">
        <div className="overline" style={{ color: "#D7A62A" }}>spend your stack</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2 leading-[0.9]">
          The
          <span style={{ color: "#76B82A" }}> Loot </span>
          Bazaar.
        </h1>
        <p className="mt-3 text-base text-[#F5F2E8]/75 max-w-xl mx-auto">
          Power-ups, sponsor coupons, gambling for fun, gifting for kindness, or
          drop your coins into the Community Tank. It's all yours.
        </p>
      </header>

      {/* Sticky balance HUD */}
      <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="bazaar-hud">
        <div className="loot-card-flat p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">Balance</div>
          <div className="font-display text-3xl mt-1" style={{ color: "#D7A62A" }} data-testid="bazaar-balance">
            <Coins size={20} className="inline mr-1" />{balance}
          </div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">Loot Coins on hand</div>
        </div>
        <div className="loot-card-flat p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">Stash streak</div>
          <div className="font-display text-3xl mt-1" style={{ color: "#E53935" }}>
            <Flame size={20} className="inline mr-1" />{streak}
          </div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">{inv?.active_streak_shields || 0} shield{(inv?.active_streak_shields || 0) === 1 ? "" : "s"} ready</div>
        </div>
        <div className="loot-card-flat p-3 col-span-2 md:col-span-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">Equipped title</div>
          <div className="font-display text-base mt-1 truncate" style={{ color: "#76B82A" }}>
            {inv?.equipped_title || "—"}
          </div>
          <Link to="/me" className="font-mono text-[10px] text-[#F5F2E8]/55 hover:text-[#D7A62A] mt-1 inline-block">
            change in profile →
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-3xl mx-auto flex flex-wrap gap-2 border-b border-[#F5F2E8]/10 pb-2" data-testid="bazaar-tabs">
        {[
          ["power-ups", "Power-Ups", <Hammer size={14} />],
          ["badges",    "Badges", <Award size={14} />],
          ["tank",      "Drop in Tank", <Heart size={14} />],
          ["flip",      "Double or Dust", <Coins size={14} />],
          ["gift",      "Loot Gifts", <Gift size={14} />],
          ["coupons",   "My Coupons", <Ticket size={14} />],
          ["auctions",  "Auctions", <Trophy size={14} />],
        ].map(([id, label, icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`bazaar-tab-${id}`}
            className="text-xs font-display uppercase tracking-widest px-3 py-1.5 border"
            style={{
              borderColor: tab === id ? "#76B82A" : "rgba(245,242,232,0.15)",
              color: tab === id ? "#76B82A" : "#F5F2E8",
              background: tab === id ? "rgba(118,184,42,0.08)" : "transparent",
            }}
          >
            <span className="inline-flex items-center gap-1.5">{icon} {label}</span>
          </button>
        ))}
      </div>

      <div className="max-w-3xl mx-auto">
        {tab === "power-ups" && <PowerUpsTab shop={shop} balance={balance} streak={streak} onAfterBuy={reload} />}
        {tab === "badges" && (
          <div className="loot-card-flat p-5 text-center">
            <Award size={28} className="inline text-[#D7A62A] mb-2" />
            <div className="font-display uppercase text-lg">Badges</div>
            <p className="font-mono text-xs text-[#F5F2E8]/65 mt-1 max-w-md mx-auto">
              Earn 'em, buy 'em, equip 'em next to your username. The full Badge Case lives at /badges.
            </p>
            <Link to="/badges" className="btn-loot text-xs inline-flex items-center gap-1 mt-3" data-testid="bazaar-badges-link">
              Open Badge Case <ArrowRight size={12} />
            </Link>
          </div>
        )}
        {tab === "tank" && <TankTab balance={balance} onAfterDonate={reload} />}
        {tab === "flip" && <FlipTab balance={balance} onAfterFlip={reload} />}
        {tab === "gift" && <GiftTab balance={balance} onAfterGift={reload} username={user?.username} />}
        {tab === "coupons" && <CouponsTab coupons={coupons} />}
        {tab === "auctions" && (
          <div className="loot-card-flat p-5 text-center">
            <Trophy size={28} className="inline text-[#D7A62A] mb-2" />
            <div className="font-display uppercase text-lg">Loot Auctions</div>
            <p className="font-mono text-xs text-[#F5F2E8]/65 mt-1 max-w-md mx-auto">
              Weekly prize on the block — everyone bids coins, top bidder wins, losers get half back. The rest goes to the Tank.
            </p>
            <Link to="/auctions" className="btn-loot text-xs inline-flex items-center gap-1 mt-3" data-testid="bazaar-auctions-link">
              See live auctions <ArrowRight size={12} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
function PowerUpsTab({ shop, balance, streak, onAfterBuy }) {
  const [busy, setBusy] = useState({});
  const buy = async (sku) => {
    setBusy({ ...busy, [sku]: true });
    try {
      const { data } = await api.post(`/coins/shop/${sku}/buy`);
      toast.success(`Bought! Balance: ${data.balance}`);
      if (data.purchase?.payload?.voucher_code) {
        toast.info(`Coupon code: ${data.purchase.payload.voucher_code}`, { duration: 6000 });
      }
      onAfterBuy();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Purchase failed");
    } finally { setBusy({ ...busy, [sku]: false }); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="bazaar-power-ups">
      {shop.map((item) => {
        const Icon = ICONS[item.icon] || Package;
        const locked = item.min_streak > 0 && streak < item.min_streak;
        const tooPoor = balance < item.cost_coins;
        const disabled = locked || tooPoor || !!busy[item.sku];
        return (
          <div key={item.sku} className="loot-card-flat p-4 space-y-2" data-testid={`shop-item-${item.sku}`}>
            <div className="flex items-start gap-3">
              <div className="p-2 border border-[#F5F2E8]/15 rounded-sm">
                <Icon size={20} style={{ color: "#D7A62A" }} />
              </div>
              <div className="flex-1">
                <div className="font-display uppercase text-sm leading-tight">{item.name}</div>
                <div className="font-mono text-[10px] text-[#F5F2E8]/65">{item.tagline}</div>
              </div>
              <div className="font-display text-lg" style={{ color: "#D7A62A" }} title="Loot Coins">
                {item.cost_coins}<span className="text-xs">c</span>
              </div>
            </div>
            <p className="text-xs text-[#F5F2E8]/75 leading-snug">{item.description}</p>
            {locked && (
              <div className="font-mono text-[10px] text-[#E53935]">
                Locked — needs Day-{item.min_streak} streak (you're on day {streak}).
              </div>
            )}
            <button
              onClick={() => buy(item.sku)}
              disabled={disabled}
              className="btn-loot w-full text-xs"
              data-testid={`shop-buy-${item.sku}`}
            >
              {busy[item.sku] ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
              {tooPoor ? `Need ${item.cost_coins - balance} more coins` : (locked ? "Locked" : `Buy for ${item.cost_coins}c`)}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// =====================================================================
function TankTab({ balance, onAfterDonate }) {
  const [amount, setAmount] = useState(100);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const dollars = (amount / 100);
  const valid = amount >= 100 && amount % 100 === 0 && amount <= balance;

  const donate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coins/donate-to-tank", { amount_coins: amount, note: note.trim() || null });
      toast.success(`Dropped $${(data.amount_cents / 100).toFixed(2)} in the Tank!`);
      onAfterDonate();
      setNote("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Donation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="loot-card-flat p-5 space-y-4" data-testid="bazaar-tank-tab">
      <div className="text-center">
        <Heart size={36} className="inline text-[#E53935] mb-2" />
        <div className="font-display uppercase text-2xl">Drop in the Tank</div>
        <p className="font-mono text-xs text-[#F5F2E8]/65 mt-1 max-w-md mx-auto">
          Convert your Loot Coins into real money for the <Link to="/tank" className="underline">Community LootFund</Link>. Funds back missions, drops, and good deeds in your city. <strong>100 coins = $1.</strong> Donate in increments of 100.
        </p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[100, 500, 1000, 2500].map((v) => (
          <button
            key={v}
            onClick={() => setAmount(v)}
            disabled={v > balance}
            data-testid={`tank-preset-${v}`}
            className="text-xs font-display uppercase tracking-widest px-2 py-2 border"
            style={{
              borderColor: amount === v ? "#76B82A" : "rgba(245,242,232,0.15)",
              color: amount === v ? "#76B82A" : (v > balance ? "rgba(245,242,232,0.35)" : "#F5F2E8"),
              background: amount === v ? "rgba(118,184,42,0.08)" : "transparent",
            }}
          >
            {v}c<br /><span className="text-[9px] opacity-70">${(v/100).toFixed(0)}</span>
          </button>
        ))}
      </div>
      <input
        type="number"
        min={100}
        step={100}
        max={balance}
        value={amount}
        onChange={(e) => setAmount(Math.max(100, parseInt(e.target.value || 100, 10)))}
        className="loot-input"
        data-testid="tank-amount"
      />
      <input
        className="loot-input"
        placeholder="Optional note (e.g. 'For the next Maine drop')"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={240}
        data-testid="tank-note"
      />
      <button
        onClick={donate}
        disabled={!valid || busy}
        className="btn-loot w-full text-xs inline-flex items-center justify-center gap-1"
        data-testid="tank-donate"
      >
        {busy ? <Loader2 size={12} className="inline animate-spin" /> : <Heart size={12} />}
        {busy ? "Dropping…" : `Donate ${amount} coins  →  $${dollars.toFixed(2)}`}
      </button>
      <p className="font-mono text-[10px] text-[#F5F2E8]/45 text-center">
        Donations show up on the public Community Tank donor list as <strong>@you · loot_coins</strong>.
      </p>
    </div>
  );
}

// =====================================================================
function FlipTab({ balance, onAfterFlip }) {
  const wagers = [10, 25, 50, 100, 250, 500];
  const [wager, setWager] = useState(50);
  const [call, setCall] = useState("heads");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const flip = async () => {
    setBusy(true);
    setLast(null);
    try {
      const { data } = await api.post("/coins/flip", { wager, call });
      setLast(data);
      onAfterFlip();
      if (data.won) toast.success(`🎉 ${data.result}! You won ${data.payout} coins.`);
      else toast.error(`😬 ${data.result}. Lost ${wager}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Flip failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="loot-card-flat p-5 space-y-4" data-testid="bazaar-flip-tab">
      <div className="text-center">
        <Coins size={36} className="inline text-[#D7A62A] mb-2" />
        <div className="font-display uppercase text-2xl">Double or Dust</div>
        <p className="font-mono text-xs text-[#F5F2E8]/65 mt-1 max-w-md mx-auto">
          Pick heads or tails. Win → get <strong>1.95×</strong> your wager back. Lose → it's gone. The 5% house edge funds the Community Tank, so even when you lose, your town wins a tiny bit.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2" data-testid="flip-call">
        {["heads", "tails"].map((c) => (
          <button
            key={c}
            onClick={() => setCall(c)}
            data-testid={`flip-call-${c}`}
            className="text-xs font-display uppercase tracking-widest px-2 py-3 border"
            style={{
              borderColor: call === c ? "#D7A62A" : "rgba(245,242,232,0.15)",
              color: call === c ? "#D7A62A" : "#F5F2E8",
              background: call === c ? "rgba(215,166,42,0.08)" : "transparent",
            }}
          >
            {c.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {wagers.map((v) => (
          <button
            key={v}
            onClick={() => setWager(v)}
            disabled={v > balance}
            data-testid={`flip-wager-${v}`}
            className="text-xs font-display uppercase tracking-widest px-2 py-2 border"
            style={{
              borderColor: wager === v ? "#76B82A" : "rgba(245,242,232,0.15)",
              color: wager === v ? "#76B82A" : (v > balance ? "rgba(245,242,232,0.35)" : "#F5F2E8"),
              background: wager === v ? "rgba(118,184,42,0.08)" : "transparent",
            }}
          >
            {v}c
          </button>
        ))}
      </div>

      <button
        onClick={flip}
        disabled={busy || balance < wager}
        className="btn-loot w-full text-xs"
        data-testid="flip-go"
      >
        {busy ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
        Flip {wager}c on {call}
      </button>

      {last && (
        <div
          className="loot-card-flat p-3 text-center border-2"
          style={{ borderColor: last.won ? "#76B82A" : "#E53935" }}
          data-testid="flip-result"
        >
          <div className="font-display uppercase text-3xl" style={{ color: last.won ? "#76B82A" : "#E53935" }}>
            {last.result.toUpperCase()}
          </div>
          <div className="font-mono text-xs text-[#F5F2E8]/75 mt-1">
            {last.won ? `Paid out ${last.payout} coins` : `Lost ${last.wager} coins`}
          </div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">New balance: {last.balance}c</div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
function GiftTab({ balance, onAfterGift, username }) {
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState(50);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState({ sent: [], received: [] });

  const reloadHistory = async () => {
    try {
      const { data } = await api.get("/coins/gifts/me");
      setHistory(data || { sent: [], received: [] });
    } catch {}
  };
  useEffect(() => { reloadHistory(); }, []);

  const send = async () => {
    if (!to.trim()) return toast.error("Who's it going to?");
    if (amount < 10 || amount > 1000) return toast.error("Amount must be 10-1000.");
    setBusy(true);
    try {
      const { data } = await api.post("/coins/gift", {
        to_username: to.trim(),
        amount,
        message: msg.trim() || null,
      });
      toast.success(`Sent ${data.amount} coins to @${data.to_username}!`);
      setTo(""); setMsg(""); setAmount(50);
      onAfterGift();
      reloadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gift failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="bazaar-gift-tab">
      <div className="loot-card-flat p-5 space-y-3">
        <div className="text-center">
          <Gift size={32} className="inline text-[#76B82A] mb-2" />
          <div className="font-display uppercase text-xl">Send a Loot Gift</div>
          <p className="font-mono text-xs text-[#F5F2E8]/65 mt-1 max-w-md mx-auto">
            Got a hunter who hooked you up? Toss them coins with a one-line note. 10-1,000 coins per gift, 5,000/day cap.
          </p>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">To (@username)</label>
          <input className="loot-input" value={to} onChange={(e) => setTo(e.target.value.replace(/^@/, ""))} placeholder="jesica" maxLength={80} data-testid="gift-to" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Amount (10-1000)</label>
          <input className="loot-input" type="number" min={10} max={1000} value={amount} onChange={(e) => setAmount(Math.max(10, Math.min(1000, parseInt(e.target.value || 10, 10))))} data-testid="gift-amount" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Message (optional)</label>
          <input className="loot-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="thx for the tip 🍕" maxLength={200} data-testid="gift-message" />
        </div>
        <button onClick={send} disabled={busy || balance < amount} className="btn-loot w-full text-xs inline-flex items-center justify-center gap-1" data-testid="gift-send">
          {busy ? <Loader2 size={12} className="inline animate-spin" /> : <Send size={12} />}
          Send {amount}c to @{to || "..."}
        </button>
      </div>

      {(history.sent.length > 0 || history.received.length > 0) && (
        <div className="loot-card-flat p-4 space-y-3">
          <div className="overline">Recent gifts</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 mb-1">Sent</div>
              {history.sent.length === 0 ? <div className="font-mono text-[10px] text-[#F5F2E8]/45">none yet</div> : history.sent.slice(0, 5).map((g) => (
                <div key={g.id} className="text-xs border-l-2 pl-2 py-1 border-[#76B82A]/30 mb-1">
                  → @{g.to_username} <span className="text-[#D7A62A]">{g.amount}c</span>
                  {g.message && <div className="font-mono text-[10px] text-[#F5F2E8]/55 truncate">"{g.message}"</div>}
                </div>
              ))}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 mb-1">Received</div>
              {history.received.length === 0 ? <div className="font-mono text-[10px] text-[#F5F2E8]/45">none yet</div> : history.received.slice(0, 5).map((g) => (
                <div key={g.id} className="text-xs border-l-2 pl-2 py-1 border-[#D7A62A]/30 mb-1">
                  ← @{g.from_username} <span className="text-[#D7A62A]">{g.amount}c</span>
                  {g.message && <div className="font-mono text-[10px] text-[#F5F2E8]/55 truncate">"{g.message}"</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
function CouponsTab({ coupons }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const issued = coupons.filter((c) => c.status === "issued");
  const others = coupons.filter((c) => c.status !== "issued");
  return (
    <div className="space-y-4" data-testid="bazaar-coupons-tab">
      <div className="loot-card-flat p-5 space-y-2">
        <div className="text-center">
          <Ticket size={32} className="inline text-[#D7A62A] mb-2" />
          <div className="font-display uppercase text-xl">My Sponsor Coupons</div>
          <p className="font-mono text-xs text-[#F5F2E8]/65 mt-1 max-w-md mx-auto">
            Each voucher is $1 off at the rotating Stash sponsor. Show the QR code at checkout — they scan it to redeem.
          </p>
        </div>
      </div>

      {coupons.length === 0 ? (
        <div className="loot-card-flat p-6 text-center font-mono text-xs text-[#F5F2E8]/55">
          No coupons yet. Buy one in the Power-Ups tab.
        </div>
      ) : (
        <>
          {issued.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {issued.map((c) => (
                <div key={c.code} className="loot-card-flat p-4 space-y-2 border-2 border-[#D7A62A]/40" data-testid={`coupon-${c.code}`}>
                  <div className="overline" style={{ color: "#D7A62A" }}>Active coupon</div>
                  <div className="font-display uppercase text-2xl">${(c.value_cents / 100).toFixed(2)} OFF</div>
                  <div className="font-mono text-[11px] text-[#F5F2E8]/65">at <strong>{c.sponsor_name || "this week's sponsor"}</strong></div>
                  <div className="bg-white p-2 inline-block">
                    <img
                      src={`${API_URL}/api/qr?url=${encodeURIComponent(c.code)}&size=10`}
                      alt={c.code}
                      className="h-32 w-32"
                      data-testid={`coupon-qr-${c.code}`}
                    />
                  </div>
                  <div className="font-mono text-xs tracking-widest text-center">{c.code}</div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55 text-center">expires {c.expires_at?.slice(0, 10)}</div>
                </div>
              ))}
            </div>
          )}
          {others.length > 0 && (
            <div className="loot-card-flat p-4 space-y-2">
              <div className="overline">History</div>
              {others.map((c) => (
                <div key={c.code} className="text-xs flex items-center justify-between border-b border-[#F5F2E8]/5 py-1">
                  <span className="font-mono">{c.code}</span>
                  <span className="font-mono text-[10px] text-[#F5F2E8]/55">{c.status} · {c.redeemed_at?.slice(0, 10) || c.expires_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
